// deps
import React, { useCallback, useEffect, useMemo } from "react";

// internal deps
import { FullWidthButton } from "@swiggy-private/dls-web-core/dist/components/FullWidthButton";
import { GenericIcon } from "@swiggy-private/dls-web-icons/dist/GenericIconWeb";
import { default as Warning } from "@swiggy-private/dls-web-icons/dist/web/generic/infoFilled/24";
import { default as ClearIcon } from "@swiggy-private/dls-web-icons/dist/web/generic/clearLight/24";

// helpers
import { isEmpty } from "@swiggy-private/js-utils";

// styles
import {
    BottomSheetContentContainer,
    LoadingContainer,
    ContainerErrorWrapper,
    ContainerBtnWrapper,
    ProgressBar,
    ApplyingText,
    ErrorText,
    CloseIconContainer,
} from "./styles";

// constants
import {
    APPLYING_COUPON_LOADER_TEXT,
    COUPON_ERROR_BOTTOM_SHEET_TEST_IDS,
    COUPONLISTING_BOTTOMSHEET_CTA,
    PAYMENT_COUPONLISTING_BOTTOMSHEET_CTA,
    REMOVING_COUPON_LOADER_TEXT,
} from "@core-ui/constants/Coupon";
import { CouponCard } from "@core-ui/components/common/coupon-card";
import { BottomSheet, BottomSheetChildrenProps } from "@core-ui/components/bottom-sheet";

//interfaces
import { CouponInfo, INextBestCoupon } from "@core-ui/interfaces/Common";
import { CouponCardImpressionPayload } from "@swiggy-private/core-types";

export interface IProps {
    errorMessage: string;
    loading?: boolean;
    nextBestCoupon: INextBestCoupon | null;
    closeBottomSheet: VoidFunction;
    reloadCouponListing: VoidFunction;
    onApplyCouponClick: (payload: CouponCardImpressionPayload) => void;
    clickMoreDetailsEvent?: (couponCode: string, couponType: string) => void;
    isRemovingCoupon?: boolean;
    isPaymentListingCouponCard?: boolean;
    showTncCouponHalfcard?: (couponInfo: CouponInfo) => void;
    onErrorHalfCardImpression?: (text: string) => void;
}

const ApplyingCouponBottomSheet = ({
    errorMessage,
    loading,
    nextBestCoupon,
    onApplyCouponClick,
    closeBottomSheet,
    reloadCouponListing,
    clickMoreDetailsEvent,
    isRemovingCoupon,
    isPaymentListingCouponCard,
    showTncCouponHalfcard,
    onErrorHalfCardImpression,
}: IProps) => {
    const ctaText = useMemo(() => {
        if (isPaymentListingCouponCard && !isEmpty(nextBestCoupon)) {
            return PAYMENT_COUPONLISTING_BOTTOMSHEET_CTA;
        }
        return COUPONLISTING_BOTTOMSHEET_CTA;
    }, [isPaymentListingCouponCard, nextBestCoupon]);

    const onCloseHandler = useCallback(() => {
        !loading && closeBottomSheet();
    }, [loading, closeBottomSheet]);

    const onBack = useCallback(() => {
        closeBottomSheet();
        reloadCouponListing();
    }, [closeBottomSheet, reloadCouponListing]);

    useEffect(() => {
        if (!isEmpty(errorMessage) && typeof onErrorHalfCardImpression === "function") {
            onErrorHalfCardImpression(errorMessage);
        }
    }, [errorMessage, onErrorHalfCardImpression]);

    return (
        <BottomSheet onClose={onCloseHandler} hideOnOverlayClick={!loading} hideCloseIcon={true}>
            {(_bProps: BottomSheetChildrenProps): React.ReactElement => (
                <BottomSheetContentContainer>
                    {loading ? (
                        <LoadingContainer>
                            <ApplyingText
                                variant="V2.heading2.neutral.black"
                                color="text_High_Emphasis"
                                data-testid={COUPON_ERROR_BOTTOM_SHEET_TEST_IDS.LOADER}
                            >
                                {isRemovingCoupon ? REMOVING_COUPON_LOADER_TEXT : APPLYING_COUPON_LOADER_TEXT}
                            </ApplyingText>
                            <ProgressBar />
                        </LoadingContainer>
                    ) : (
                        <>
                            {isPaymentListingCouponCard && (
                                <CloseIconContainer>
                                    <GenericIcon
                                        onClick={onCloseHandler}
                                        Icon={ClearIcon}
                                        fillColor="text_Low_Emphasis"
                                        strokeColor="text_Low_Emphasis"
                                    />
                                </CloseIconContainer>
                            )}
                            <ContainerErrorWrapper data-testid={COUPON_ERROR_BOTTOM_SHEET_TEST_IDS.CONTAINER}>
                                {!isPaymentListingCouponCard && <GenericIcon Icon={Warning} fillColor="negative" />}
                                <ErrorText
                                    data-testid={COUPON_ERROR_BOTTOM_SHEET_TEST_IDS.TITLE}
                                    variant="V2.heading2.neutral.black"
                                    color="text_High_Emphasis"
                                    isPaymentListingCouponCard={isPaymentListingCouponCard}
                                >
                                    {errorMessage}
                                </ErrorText>
                            </ContainerErrorWrapper>
                            {nextBestCoupon && (
                                <CouponCard
                                    data={nextBestCoupon.coupon}
                                    type={nextBestCoupon.type}
                                    onApplyCouponClick={onApplyCouponClick}
                                    clickMoreDetailsEvent={clickMoreDetailsEvent}
                                    isCouponSuggestionFlow={true}
                                    showTncCouponHalfcard={showTncCouponHalfcard}
                                    isPaymentListingCouponCard={isPaymentListingCouponCard}
                                />
                            )}
                            <ContainerBtnWrapper data-testid={COUPON_ERROR_BOTTOM_SHEET_TEST_IDS.FULL_WIDTH_CTA}>
                                <FullWidthButton
                                    variant={isPaymentListingCouponCard ? "tertiary" : "primary"}
                                    onClick={onBack}
                                >
                                    {ctaText}
                                </FullWidthButton>
                            </ContainerBtnWrapper>
                        </>
                    )}
                </BottomSheetContentContainer>
            )}
        </BottomSheet>
    );
};

export default ApplyingCouponBottomSheet;
